import {
  INVENTORY_CONDITION_DEMO,
  INVENTORY_ITEM_CONDITIONS
} from "constants/inventory-products";
import {
  BATTERY_SUBTYPE,
  CHARGER_SUBTYPE,
  DOME_SUBTYPE,
  EAR_MOLD_SUBTYPE,
  EAR_TYPES,
  LEFT_EAR_TYPE,
  OTHER_SUBTYPE,
  RECEIVER_SUBTYPE,
  REMOTE_SUBTYPE,
  RIGHT_EAR_TYPE,
  TUBE_SUBTYPE
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  SERVICE_PRODUCT_TYPE
} from "constants/sales";
import type { LineItem, Sale } from "services/types/sales.type";
import { formatMoneyValue } from "utils/common";
import { getLineItemSerialRaw } from "utils/line-item-display.util";

import {
  type DeviceKind,
  type EarKind,
  type EarSlot,
  type NumberedKind,
  PA_EAR_KINDS,
  PA_NUMBERED_KINDS,
  earSlotFieldTags,
  kindTotalFieldTags,
  numberedFieldTags,
  paEarTag,
  paKindTotalTag,
  paNumberedTag
} from "../merge-tags-options/purchase-agreement-device-tags.utils";
import {
  getCatalogName,
  getConfigurableLineItemValue
} from "./common/configurable-merge-tags";
import {
  excludeReturnedOrExchanged,
  getLineItemDiscountTotal,
  getLineItemNetTotal,
  getSortedItems,
  isReturnedOrExchanged
} from "./common/utils";
import type { MergeTagsReplaceHandler } from "./merge-tags-values";
import {
  getHearingAidLossDamageWarrantyEnd,
  getHearingAidSerialNumber,
  getHearingAidServicePlanExpiration,
  getHearingAidWarrantyEnd,
  getLineItemEar
} from "./patient-selected-invoice-hearing-aids.utils";

/*
 * ENG-2492 device values (options in merge-tags-options/purchase-agreement-device-tags.utils.ts). Each
 * resolves from the invoice the form is printed for (`data.contextSale`) and prints "" when there is none.
 *
 * - A slot's line is the first of its kind whose ear matches, skipping returned and exchanged lines: the line
 *   the existing per-ear tags pick (getFirstHearingAidFromSale, findAccessoryItem), so a form never describes
 *   two devices on one side. No side is the first line of the kind whose ear is neither.
 * - A value the invoice table prints comes from the table's own cell (getConfigurableLineItemValue).
 * - A cell holding several values is split (tech plan D1): the price cell's net, list and discount, and the
 *   responsibility cell's patient and insurance amounts, are each a tag, money only.
 * - Dates print MM/dd/yyyy, like the existing per-ear tags beside them (D9), not the table's "MMM dd, yyyy".
 * - Values only Anvil fills follow suno-backend billing/views/purchase_agreement.py
 *   (generate_device_specific_data), which is the live purchase agreement context.
 */

const isAccessory = (item: LineItem, subtype: number) => {
  const master = item.product?.master_product;
  return master?.type === ACCESSORY_PRODUCT_TYPE && master.subtype === subtype;
};

const KIND_MATCH: Record<DeviceKind, (item: LineItem) => boolean> = {
  HEARING_AID: (item) =>
    item.product?.master_product?.type === HEARING_AID_PRODUCT_TYPE,
  EARMOLD: (item) => isAccessory(item, EAR_MOLD_SUBTYPE),
  RECEIVER: (item) => isAccessory(item, RECEIVER_SUBTYPE),
  DOME: (item) => isAccessory(item, DOME_SUBTYPE),
  TUBE: (item) => isAccessory(item, TUBE_SUBTYPE),
  BATTERY: (item) => isAccessory(item, BATTERY_SUBTYPE),
  CHARGER: (item) => isAccessory(item, CHARGER_SUBTYPE),
  REMOTE: (item) => isAccessory(item, REMOTE_SUBTYPE),
  OTHER: (item) => isAccessory(item, OTHER_SUBTYPE)
};

// The invoice table's configuration prefix for each kind (`<prefix>_show_<column>`).
const TABLE_PREFIX: Record<DeviceKind, string> = {
  HEARING_AID: "hearing_aids",
  EARMOLD: "earmolds",
  RECEIVER: "receivers",
  DOME: "domes",
  TUBE: "tubes",
  BATTERY: "batteries",
  CHARGER: "chargers",
  REMOTE: "remotes",
  OTHER: "others"
};

const SLOT_EAR: Record<EarSlot, string> = {
  LEFT: LEFT_EAR_TYPE,
  RIGHT: RIGHT_EAR_TYPE,
  NO_SIDE: ""
};

export const getSlotLine = (
  sale: Sale | undefined,
  kind: EarKind,
  slot: EarSlot
) =>
  (sale?.items as LineItem[] | undefined)?.find((item) => {
    if (isReturnedOrExchanged(item) || !KIND_MATCH[kind](item)) return false;
    const ear = getLineItemEar(item);
    if (slot !== "NO_SIDE") return ear === SLOT_EAR[slot];
    return ear !== LEFT_EAR_TYPE && ear !== RIGHT_EAR_TYPE;
  });

// A numbered kind's lines in the invoice table's own order.
export const getNumberedLine = (
  sale: Sale | undefined,
  kind: NumberedKind,
  position: number
) =>
  getSortedItems(excludeReturnedOrExchanged(sale?.items)).filter(
    KIND_MATCH[kind]
  )[position - 1];

// One invoice-table cell, with only that column of that kind switched on.
const tableCell = (
  item: LineItem,
  kind: DeviceKind | null,
  header: string,
  column?: string
) =>
  getConfigurableLineItemValue(
    item,
    header,
    kind && column ? { [`${TABLE_PREFIX[kind]}_show_${column}`]: true } : {}
  );

// Signed: a single blank has no other column to show that a line is a credit.
const money = (value: number) => formatMoneyValue(value, value < 0);

const conditionLabel = (condition: number | null | undefined) =>
  condition === INVENTORY_CONDITION_DEMO
    ? "Demo"
    : INVENTORY_ITEM_CONDITIONS.getFromCode(condition);

/** Values any invoice line has, whatever its kind (devices and services alike). */
export const LINE_VALUES: Record<string, (item: LineItem) => string> = {
  Product: (item) => tableCell(item, null, "Product"),
  CPTCode: (item) => tableCell(item, null, "CPT Code"),
  Quantity: (item) => String(Number(item.quantity) || 1),
  UnitPrice: (item) => money(Number(item.price) || 0),
  ListPrice: (item) =>
    money((Number(item.price) || 0) * (Number(item.quantity) || 1)),
  Discount: (item) => money(getLineItemDiscountTotal(item)),
  Price: (item) => money(getLineItemNetTotal(item)),
  TotalWithTax: (item) => money(Number(item.total) || 0),
  PatientResponsibility: (item) =>
    money(Number(item.patient_responsibility) || 0),
  InsuranceResponsibility: (item) =>
    money(Number(item.insurer_responsibility) || 0)
};

type LineValue = (item: LineItem, ear: string, kind: DeviceKind) => string;

/** Every device field, for any kind; a kind only offers the fields its spec lists. */
export const DEVICE_LINE_VALUES: Record<string, LineValue> = {
  ...LINE_VALUES,
  Manufacturer: (item) =>
    item.product?.master_product?.manufacturer?.name ?? "",
  Model: (item) => item.product?.master_product?.model ?? "",
  Name: (item) => getCatalogName(item),
  SerialNumber: (item, ear, kind) =>
    kind === "HEARING_AID"
      ? getHearingAidSerialNumber(item, ear)
      : getLineItemSerialRaw(item),
  WarrantyEnd: (item, ear) => getHearingAidWarrantyEnd(item, ear),
  // The line's own ear, once. The table's Ear cell joins every patient product's ear, so a line holding
  // two left products shows "Left, Left" there.
  Ear: (item) => EAR_TYPES.getFromCode(getLineItemEar(item)),
  // The stocked item's colour, which is what the table's Color column prints for the kinds that have one.
  Color: (item) => item.inventory_product?.color ?? "",
  Battery: (item, _ear, kind) => tableCell(item, kind, "Battery", "battery"),
  Condition: (item) => conditionLabel(item.inventory_product?.condition),
  LossDamageWarrantyEnd: (item, ear) =>
    getHearingAidLossDamageWarrantyEnd(item, ear),
  LossDamageDeductible: (item, _ear, kind) =>
    tableCell(item, kind, "L&D Deductible", "lnd_deductible"),
  ServicePlanEnd: (item, ear) => getHearingAidServicePlanExpiration(item, ear),
  Material: (item, _ear, kind) => tableCell(item, kind, "Material", "material"),
  CanalLength: (item, _ear, kind) =>
    tableCell(item, kind, "Canal Length", "canal_length"),
  CanalVenting: (item, _ear, kind) =>
    tableCell(item, kind, "Canal Venting", "canal_venting"),
  Gain: (item, _ear, kind) => tableCell(item, kind, "Gain", "gain"),
  Length: (item, _ear, kind) => tableCell(item, kind, "Length", "length"),
  Type: (item, _ear, kind) => tableCell(item, kind, "Dome Type", "dome_type"),
  Size: (item, _ear, kind) => tableCell(item, kind, "Dome Size", "dome_size")
};

export const getEarSlotValue = (
  sale: Sale | undefined,
  kind: EarKind,
  slot: EarSlot,
  field: string
): string => {
  const item = getSlotLine(sale, kind, slot);
  if (!item) return "";
  return DEVICE_LINE_VALUES[field](item, SLOT_EAR[slot], kind) ?? "";
};

/** ENG-2501's name for the hearing-aid case. */
export const getHearingAidSlotValue = (
  sale: Sale | undefined,
  slot: EarSlot,
  field: string
) => getEarSlotValue(sale, "HEARING_AID", slot, field);

// Python's str.title(), which Anvil applies to the colour in `device_options`.
const titleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/(^|[^a-z])([a-z])/g, (_, p, c) => p + c.toUpperCase());

const yearOf = (date: string | null | undefined) =>
  date ? Number(date.slice(0, 4)) : NaN;

const sumLines = (lines: LineItem[], value: (item: LineItem) => number) =>
  lines.reduce((total, item) => total + value(item), 0);

const months = (value: number | null | undefined) =>
  typeof value === "number" ? `${value} months` : "";

type KindValue = (sale: Sale, kind: DeviceKind) => string;

const kindLines = (sale: Sale, kind: DeviceKind) =>
  excludeReturnedOrExchanged(sale.items).filter(KIND_MATCH[kind]);

// Anvil keeps the last line's product for the kind-level values.
const lastLine = (sale: Sale, kind: DeviceKind) => kindLines(sale, kind).at(-1);

const moneyIfAny = (
  sale: Sale,
  kind: DeviceKind,
  value: (item: LineItem) => number
) => {
  const lines = kindLines(sale, kind);
  return lines.length ? money(sumLines(lines, value)) : "";
};

/** Anvil's values about a kind as a whole (`<kind>_make_model`, `<kind>_warranty_repair`, …). */
export const KIND_TOTAL_VALUES: Record<string, KindValue> = {
  MakeAndModel: (sale, kind) => {
    const master = lastLine(sale, kind)?.product?.master_product;
    return master
      ? [master.manufacturer?.name, master.model].filter(Boolean).join(" ")
      : "";
  },
  Manufacturer: (sale, kind) =>
    lastLine(sale, kind)?.product?.master_product?.manufacturer?.name ?? "",
  Model: (sale, kind) =>
    lastLine(sale, kind)?.product?.master_product?.model ?? "",
  // Hearing aids only (the only kind whose Both group offers it).
  Options: (sale) => {
    const left = getSlotLine(sale, "HEARING_AID", "LEFT")?.inventory_product;
    const right = getSlotLine(sale, "HEARING_AID", "RIGHT")?.inventory_product;
    const color = left?.color || right?.color;
    const battery = left?.battery || right?.battery;
    return [
      color ? `Color: ${titleCase(color)}` : "",
      battery ? `Battery: ${battery}` : ""
    ]
      .filter(Boolean)
      .join("  ");
  },
  RepairWarranty: (sale, kind) =>
    months(lastLine(sale, kind)?.product?.specification?.warranty_months),
  LossDamageWarranty: (sale, kind) =>
    months(
      lastLine(sale, kind)?.product?.specification?.loss_damage_warranty_months
    ),
  LossDamageDeductible: (sale, kind) => {
    const last = lastLine(sale, kind);
    return last
      ? tableCell(last, kind, "L&D Deductible", "lnd_deductible")
      : "";
  },
  ListTotal: (sale, kind) =>
    moneyIfAny(
      sale,
      kind,
      (item) => (Number(item.price) || 0) * (Number(item.quantity) || 1)
    ),
  TotalWithTax: (sale, kind) =>
    moneyIfAny(sale, kind, (item) => Number(item.total) || 0),
  TotalPlusServices: (sale, kind) => {
    if (!kindLines(sale, kind).length) return "";
    const services = excludeReturnedOrExchanged(sale.items).filter(
      (item) => item.product?.master_product?.type === SERVICE_PRODUCT_TYPE
    );
    return money(
      sumLines(kindLines(sale, kind), (item) => Number(item.total) || 0) +
        sumLines(services, (item) => Number(item.total) || 0)
    );
  },
  // The manufacturer warranty's end year less the sale's service year, as Anvil computes it: from the first
  // or second stocked line of the kind (its `devices[0]` / `devices[1]`), whatever their ears.
  WarrantyYears: (sale, kind) => {
    const [first, second] = kindLines(sale, kind).filter(
      (item) => item.inventory_product
    );
    const end =
      first?.inventory_product?.manufacturer_warranty_expiration_date ||
      second?.inventory_product?.manufacturer_warranty_expiration_date;
    const years = yearOf(end) - yearOf(sale.service_date);
    return Number.isFinite(years) && years ? String(years) : "";
  }
};

/** ENG-2501's name for the hearing-aid kind-level values. */
export const BOTH_AIDS_VALUES: Record<string, (sale: Sale) => string> =
  Object.fromEntries(
    kindTotalFieldTags("HEARING_AID").map((field) => [
      field,
      (sale: Sale) => KIND_TOTAL_VALUES[field](sale, "HEARING_AID")
    ])
  );

type Handler = MergeTagsReplaceHandler[string];

const EAR_KINDS = Object.keys(PA_EAR_KINDS) as EarKind[];
const NUMBERED_KINDS = Object.keys(PA_NUMBERED_KINDS) as NumberedKind[];
const EAR_SLOTS: EarSlot[] = ["LEFT", "RIGHT", "NO_SIDE"];

const kindTotalHandlers = (kind: DeviceKind) =>
  kindTotalFieldTags(kind).map((field): [string, Handler] => [
    paKindTotalTag(kind, field),
    ({ data }) =>
      data.contextSale ? KIND_TOTAL_VALUES[field](data.contextSale, kind) : ""
  ]);

export const PURCHASE_AGREEMENT_TAG_VALUES: MergeTagsReplaceHandler =
  Object.fromEntries([
    ...EAR_KINDS.flatMap((kind) => [
      ...EAR_SLOTS.flatMap((slot) =>
        earSlotFieldTags(kind, slot).map((field): [string, Handler] => [
          paEarTag(kind, slot, field),
          ({ data }) => getEarSlotValue(data.contextSale, kind, slot, field)
        ])
      ),
      ...kindTotalHandlers(kind)
    ]),
    ...NUMBERED_KINDS.flatMap((kind) => [
      ...Array.from({ length: PA_NUMBERED_KINDS[kind].count }, (_, index) =>
        numberedFieldTags(kind).map((field): [string, Handler] => [
          paNumberedTag(kind, index + 1, field),
          ({ data }) => {
            const item = getNumberedLine(data.contextSale, kind, index + 1);
            return item
              ? (DEVICE_LINE_VALUES[field](item, "", kind) ?? "")
              : "";
          }
        ])
      ).flat(),
      ...kindTotalHandlers(kind)
    ])
  ]);
